<template>
  <div>
    <p class="mt-6 mb-4 txt-body-m text-subtle" data-testid="leads-intro" v-html="t('Leads.intro', { name: contactableName })"></p>
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="w-fit" data-testid="leads-tabs">
        <TabBar :model-value="selectedType" :tabs="tabItems" @update:model-value="onTabChange" />
      </div>
      <div class="flex items-center gap-4">
        <ToggleSwitch :model-value="archived" size="s" data-testid="leads-archived-toggle" @update:model-value="onArchivedToggle">
          {{ t('Leads.filter.archived') }}
        </ToggleSwitch>
        <ReviewsFilterMultiSelect
          :model-value="statuses"
          :options="statusOptions"
          :label="t('Leads.filter.status.filtername')"
          data-testid="leads-filter-status"
          @update:model-value="(value: string[]) => onStatusesChange(value as LeadStatusEnum[])"
        >
          <template #header="{ close }">
            <div class="flex w-48 items-center justify-between bg-surface-elevation-4 px-2 py-2 txt-body-s-bold">
              {{ t('Leads.filter.status.headline') }}
              <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
            </div>
          </template>
        </ReviewsFilterMultiSelect>
      </div>
    </div>
    <Teleport defer :to="EXPORT_ACTION_TARGET">
      <button
        v-if="!leads.length"
        type="button"
        class="btn-disabled btn-outline-color-s whitespace-nowrap"
        disabled
        data-testid="leads-export-button"
      >
        {{ t('Leads.export.action') }}
      </button>
      <button
        v-else-if="canExport"
        type="button"
        class="btn-outline-color-s whitespace-nowrap"
        :class="{ 'btn-disabled': exporting }"
        :disabled="exporting"
        data-testid="leads-export-button"
        @click="emit('export')"
      >
        {{ t('Leads.export.action') }}
        <DownloadIcon class="lucide-icon-s" />
      </button>
      <TooltipWrapper v-else top>
        <template #trigger>
          <button type="button" class="btn-disabled btn-outline-color-s whitespace-nowrap" disabled data-testid="leads-export-button">
            <LockIcon class="lucide-icon-s" />
            {{ t('Leads.export.action') }}
          </button>
        </template>
        <template #content>
          <div data-testid="leads-export-fenced-hint">{{ t('Leads.export.fencedHint') }}</div>
        </template>
      </TooltipWrapper>
    </Teleport>
    <div class="overflow-x-auto py-6">
      <TableLight class="w-full table-fixed">
        <thead>
          <tr>
            <TableH class="w-48" :label="t('Leads.table.type')" />
            <th class="text-start">
              <div class="pl-4">{{ t('Leads.table.name') }}</div>
            </th>
            <TableH :label="t('Leads.table.company')" />
            <TableH :label="t('Leads.table.email')" />
            <TableH class="w-36" :label="t('Leads.table.status')" />
            <TableH class="w-44 whitespace-nowrap" :label="t('Leads.table.receivedAt')" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="leads.length === 0 && !loading">
            <td colspan="6" class="text-center">
              <p class="py-12 txt-body-m text-subtlest" data-testid="leads-empty-state">
                {{ emptyStateText }}
              </p>
            </td>
          </tr>
          <tr
            v-for="lead in leads"
            :key="lead.id ?? lead.senderEmail"
            tabindex="0"
            role="button"
            class="cursor-pointer"
            :class="{ 'blur-xs select-none': lead.locked }"
            :data-testid="`leads-row-${lead.id}`"
            @click="onRowClick(lead)"
            @keydown.enter="onRowClick(lead)"
            @keydown.space.prevent="onRowClick(lead)"
          >
            <td class="text-left">
              <ChipBadge
                v-if="lead.contactRequestType"
                :color="badgeColorByType[lead.contactRequestType]"
                rounded
                :data-testid="`leads-type-${lead.id}`"
              >
                {{ t(`Leads.type.${lead.contactRequestType}`) }}
              </ChipBadge>
              <span v-else>---</span>
            </td>
            <td class="text-left">
              <div class="truncate pl-4">{{ lead.senderName || '---' }}</div>
            </td>
            <td class="truncate text-left">{{ lead.senderCompany || '---' }}</td>
            <td class="text-left">
              <div v-if="lead.locked" class="truncate" :data-testid="`leads-email-${lead.id}`">{{ lead.senderEmail }}</div>
              <div v-else class="flex items-center gap-1">
                <a
                  class="truncate"
                  :href="`mailto:${lead.senderEmail}`"
                  :data-testid="`leads-email-${lead.id}`"
                  @click.stop="trackLeadContact('email', 'leads_list', 'mailto')"
                  >{{ lead.senderEmail }}</a
                >
                <TooltipWrapper top>
                  <template #trigger>
                    <IconButton
                      :icon="CopyIcon"
                      size="extra-small"
                      variant="ghost-neutral"
                      :aria-label="t('Leads.action.copyEmail')"
                      :data-testid="`leads-copy-email-${lead.id}`"
                      @click.stop="onCopyEmail(lead.senderEmail)"
                    />
                  </template>
                  <template #content>
                    <div>{{ t('Leads.action.copyEmail') }}</div>
                  </template>
                </TooltipWrapper>
              </div>
            </td>
            <td class="text-left">
              <ChipBadge
                v-if="lead.leadStatus"
                :color="badgeColorByStatus[lead.leadStatus]"
                rounded
                :data-testid="`leads-status-${lead.id}`"
              >
                {{ t(leadStatusLabelKey(lead.leadStatus)) }}
              </ChipBadge>
              <span v-else>---</span>
            </td>
            <td class="text-left whitespace-nowrap">{{ lead.createdAt ? d(lead.createdAt, 'dateTimeShort') : '---' }}</td>
          </tr>
        </tbody>
      </TableLight>
      <div v-if="loading" class="mt-6 flex justify-center">
        <CircleSpinner />
      </div>
      <PaginationControl
        v-if="leads.length > 0"
        :page="pagination.page"
        :per-page="pagination.perPage"
        :total="pagination.total"
        show-range
        show-total
        show-pagination="always"
        @update:page="emit('update:page', $event)"
        @update:per-page="emit('update:perPage', $event)"
      />
    </div>
    <OutlinkFencingModal
      ref="upsellModal"
      :headline="t('Leads.upsell.title')"
      :body="t('Leads.upsell.body', { name: contactableName })"
      :cta-primary-label="t('Leads.upsell.upgradeCta')"
      :cta-primary-link="t('Leads.upsell.ctaLink')"
      :cta-primary-icon="LockIcon"
      :cta-secondary-label="t('Leads.upsell.dismiss')"
      @cta-primary-click="trackFenceCta('leads_upsell_modal')"
    />
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, CircleSpinner, IconButton, TabBar, ToggleSwitch, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CopyIcon, DownloadIcon, LockIcon, XIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import PaginationControl from '@/components/PaginationControl.vue'
import OutlinkFencingModal from '@/components/ReviewsCommon/Fencing/OutlinkFencingModal.vue'
import ReviewsFilterMultiSelect from '@/components/ReviewsManagement/Reviews/ReviewsFilterMultiSelect.vue'
import { LeadsContactRequestTypeEnum, type LeadStatusEnum } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import { trackPlanUpgrade } from '@/services/planCtaTracking'
import { TableH, TableLight } from '@/ui/TableLight'

import { EXPORT_ACTION_TARGET } from './exportActionTarget'
import { trackLeadContact } from './leadContactTracking'
import { badgeColorByStatus, leadStatusLabelKey, leadStatusOrder } from './leadStatus'
import { badgeColorByType, copyEmail } from './leadType'
import type { Lead, LeadsContext, LeadsFacet, LeadsPagination } from './types'

const props = defineProps<{
  leads: Lead[]
  facets: LeadsFacet[]
  pagination: LeadsPagination
  selectedType: LeadsContactRequestTypeEnum
  archived: boolean
  statuses: LeadStatusEnum[]
  context: LeadsContext
  contactableName: string
  loading?: boolean
  exporting?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:selectedType', value: LeadsContactRequestTypeEnum): void
  (e: 'update:archived', value: boolean): void
  (e: 'update:statuses', value: LeadStatusEnum[]): void
  (e: 'update:page', value: number): void
  (e: 'update:perPage', value: number): void
  (e: 'export'): void
}>()

const { t, d } = useI18n()
const route = useRoute()
const router = useRouter()

const emptyStateKeyPrefix = computed(() => (props.context === 'agency' ? 'AgencyLeads' : 'ProductLeads'))

const emptyStateText = computed(() => {
  if (props.archived || props.statuses.length > 0) {
    return t('Leads.emptyState.noMatchesDescription')
  }
  if (props.selectedType === LeadsContactRequestTypeEnum.All) {
    return t(`${emptyStateKeyPrefix.value}.emptyState.description`, { contactableName: props.contactableName })
  }
  return t('Leads.emptyState.filteredDescription')
})

// Page-scoped, so it can fence a vendor whose entitled type sits on a later page. Fails closed.
const canExport = computed(() => props.leads.some((lead) => !lead.locked))

const tabItems = computed(() =>
  [
    LeadsContactRequestTypeEnum.All,
    LeadsContactRequestTypeEnum.ContactForm,
    LeadsContactRequestTypeEnum.CustomQuote,
    LeadsContactRequestTypeEnum.FileDownload,
    LeadsContactRequestTypeEnum.ReviewStarter,
  ]
    .filter(
      (type) =>
        props.context !== 'agency' ||
        (type !== LeadsContactRequestTypeEnum.CustomQuote && type !== LeadsContactRequestTypeEnum.ReviewStarter),
    )
    .map((type) => ({
      id: type,
      name: t(`Leads.tabs.${type}`, { count: props.facets.find((facet) => facet.key === type)?.docCount ?? 0 }),
    })),
)

function onTabChange(value: string) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: 'lead_type',
    filter_type: 'button',
    filter_name: 'lead_type',
    filter_value: value,
  })
  emit('update:selectedType', value as LeadsContactRequestTypeEnum)
}

const statusOptions = computed(() =>
  leadStatusOrder.map((value) => ({
    text: t(leadStatusLabelKey(value)),
    value,
    renderType: 'stateBadges',
    color: badgeColorByStatus[value],
  })),
)

function onStatusesChange(value: LeadStatusEnum[]) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: 'lead_status',
    filter_type: 'tickbox',
    filter_name: 'status',
    filter_value: value.join(','),
  })
  emit('update:statuses', value)
}

function onArchivedToggle(value: boolean) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: 'lead_archived',
    filter_type: 'tickbox',
    filter_name: 'lead_archived',
    filter_value: value ? 'archived' : 'active',
  })
  emit('update:archived', value)
}

const upsellModal = ref<InstanceType<typeof OutlinkFencingModal> | null>(null)

function onCopyEmail(email: string) {
  copyEmail(email)
  trackLeadContact('email', 'leads_list', 'copy')
}

// Routed through the shared fence helper so the leads fence carries entity_type/entity_id
// like every other fence in the app; the leads fence predated it and fired a bare cta_click.
function trackFenceCta(placement: string) {
  trackPlanUpgrade({
    entity_type: props.context === 'agency' ? 'service' : 'tool',
    entity_id: String(route.params.agencyId ?? route.params.productId ?? ''),
    placement,
  })
}

function onRowClick(lead: Lead) {
  if (lead.locked) {
    trackFenceCta('leads_locked_row')
    upsellModal.value?.show()
    return
  }

  if (!lead.id) return

  router.push({
    name: props.context === 'agency' ? ReviewsRouteName.ReviewsAgencyLeadDetail : ReviewsRouteName.ReviewsProductLeadDetail,
    params: { ...route.params, leadId: lead.id },
  })
}
</script>
